'use client';

import { useState, type CSSProperties } from 'react';
import { TONE, type Tone } from '@leanin/ui';

import { toneFromId } from '../../lib/group-tone';
import styles from './GroupLogoBlock.module.css';

const FALLBACK_TONE_CYCLE: ReadonlyArray<Tone> = [
  'burgundy',
  'yellow',
  'emerald',
  'navy',
  'orange',
];

export type GroupLogoSize = 96 | 72 | 48;

const INITIALS_FONT_SIZE: Record<GroupLogoSize, number> = { 96: 32, 72: 24, 48: 16 };

// The custom properties `GroupLogoBlock.module.css` reads. `compactSize` is the
// size while a PageHeader stacks the logo above its title; omitted, the logo
// keeps `size` everywhere.
export function groupLogoSizeVars(
  size: GroupLogoSize,
  compactSize: GroupLogoSize = size,
): CSSProperties {
  return {
    '--logo-size': `${size}px`,
    '--logo-size-compact': `${compactSize}px`,
    '--logo-font': `${INITIALS_FONT_SIZE[size]}px`,
    '--logo-font-compact': `${INITIALS_FONT_SIZE[compactSize]}px`,
  } as CSSProperties;
}

// Brand block for any group (circle / network / space). Renders the uploaded
// image when present; otherwise an id-derived tonal background with the
// group's italic initials. There is no user-facing "pick a tone" affordance —
// the fallback tone is purely a deterministic visual variety knob.
//
// If the upload fails to load (404, ORB-blocked CDN response, broken link), the
// component falls back to the initials block — without this, the browser
// renders the literal alt text in the empty <img> rect, which reads as broken.
export function GroupLogoBlock({
  logoUrl,
  groupId,
  name,
  size = 96,
  compactSize,
}: {
  logoUrl: string | null;
  groupId: string;
  name: string;
  size?: GroupLogoSize;
  compactSize?: GroupLogoSize;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const sizeVars = groupLogoSizeVars(size, compactSize);

  if (logoUrl && !imgFailed) {
    return (
      <div
        className={styles.block}
        style={{
          ...sizeVars,
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <img
          src={logoUrl}
          alt={name}
          width={size}
          height={size}
          onError={() => setImgFailed(true)}
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
      </div>
    );
  }

  const tone = toneFromId(groupId, FALLBACK_TONE_CYCLE);
  const swatch = TONE[tone];

  return (
    <div
      className={`${styles.block} ${styles.initials}`}
      style={{
        ...sizeVars,
        borderRadius: 'var(--radius-md)',
        background: swatch.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: swatch.ink,
      }}
    >
      {initials}
    </div>
  );
}
